import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  deriveTagFromName,
  normalizeVariationName,
  uniqueTag,
  validateVariationName,
} from "@/lib/variationName";

export interface Variation {
  id: string;
  version_id: string;
  name: string;
  tag: string;
  html_content: string | null;
  subject: string | null;
  pending: boolean;
  created_at: string;
}

const MAX_VARIATIONS_PER_VERSION = 10;

// Kept for backwards-compatible imports; new code should use deriveTagFromName.
export function sanitizeVariationTag(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "")
    .substring(0, 50);
}

export function useVariations(versionId: string | null | undefined) {
  const queryClient = useQueryClient();

  const { data: variations, isLoading, refetch } = useQuery({
    queryKey: ["variations", versionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("variations")
        .select("*")
        .eq("version_id", versionId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as Variation[];
    },
    enabled: !!versionId,
    // Poll every 5s while any variation is awaiting its inbound email so the
    // UI flips out of the "awaiting" state as soon as the webhook clears it.
    refetchInterval: (query) => {
      const data = query.state.data as Variation[] | undefined;
      if (data?.some((v) => v.pending)) return 5000;
      return false;
    },
  });

  const createPendingVariation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!versionId) throw new Error("No version selected");

      const validationError = validateVariationName(name);
      if (validationError) throw new Error(validationError);

      const normalized = normalizeVariationName(name);
      const existingByName = (variations || []).find(
        (v) => v.name.toLowerCase() === normalized.toLowerCase()
      );
      if (existingByName) {
        throw new Error(`A variation named "${normalized}" already exists`);
      }

      const tag = uniqueTag(
        deriveTagFromName(normalized),
        (variations || []).map((v) => v.tag)
      );

      const { data, error } = await supabase
        .from("variations")
        .insert({
          version_id: versionId,
          name: normalized,
          tag,
          pending: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Variation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["variations", versionId] });
    },
    onError: (error: Error) => {
      const msg = error.message || "";
      if (msg.includes("variation_limit_exceeded")) {
        toast.error("You've reached your plan's variation limit for this version.", {
          action: {
            label: "Upgrade plan",
            onClick: () => {
              window.location.href = "/plans";
            },
          },
        });
        return;
      }
      if (msg.includes("unique_version_tag") || msg.includes("duplicate key")) {
        toast.error("A variation with this tag already exists for this version.");
        return;
      }
      toast.error(msg || "Failed to create variation");
    },
  });

  const cancelPendingVariation = useMutation({
    mutationFn: async (variationId: string) => {
      const target = (variations || []).find((v) => v.id === variationId);
      if (!target) throw new Error("Variation not found");
      if (!target.pending) {
        throw new Error("Only pending variations can be cancelled");
      }

      const { error } = await supabase
        .from("variations")
        .delete()
        .eq("id", variationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["variations", versionId] });
      toast.success("Pending variation cancelled");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to cancel variation");
    },
  });

  const renameVariation = useMutation({
    mutationFn: async ({ variationId, newName }: { variationId: string; newName: string }) => {
      const validationError = validateVariationName(newName);
      if (validationError) throw new Error(validationError);

      const normalized = normalizeVariationName(newName);
      const collision = variations?.find(
        (v) => v.id !== variationId && v.name.toLowerCase() === normalized.toLowerCase()
      );
      if (collision) {
        throw new Error(`A variation named "${normalized}" already exists`);
      }

      const { error } = await supabase
        .from("variations")
        .update({ name: normalized })
        .eq("id", variationId);

      if (error) throw error;
      return normalized;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["variations", versionId] });
      toast.success("Variation renamed");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to rename variation");
    },
  });

  const deleteVariation = useMutation({
    mutationFn: async (variationId: string) => {
      const { error } = await supabase
        .from("variations")
        .delete()
        .eq("id", variationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["variations", versionId] });
      queryClient.invalidateQueries({ queryKey: ["comments"] });
      toast.success("Variation deleted");
    },
    onError: () => {
      toast.error("Failed to delete variation");
    },
  });

  return {
    variations: variations || [],
    isLoading,
    refetch,
    createPendingVariation,
    cancelPendingVariation,
    renameVariation,
    deleteVariation,
  };
}
